/**
 * Exchange Rates — the rates Finance maintains, and claims convert at.
 *
 * A rate here decides how much money leaves the company, so the page is built
 * to be read as much as edited: every rate shows who last changed it and when,
 * and nothing is pre-filled with a guess. A currency with no rate is not an
 * inconvenience to be worked around — receipts in it are blocked until someone
 * accountable enters a number.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { fmtDateTime, formatRate, HOME_CURRENCY, type FxRate } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { ErrorNote, QueuePanel } from '@/components/QueueKit';
import { LoadingBlock } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import '@/styles/pages.css';

interface RatesResponse {
  homeCurrency: string;
  rates: FxRate[];
  canEdit: boolean;
}

export function ExchangeRatesPage(): ReactNode {
  const { toast } = useToast();
  const [data, setData] = useState<RatesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [currency, setCurrency] = useState('');
  const [rate, setRate] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await api.fx.list<RatesResponse>());
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async (): Promise<void> => {
    const code = currency.trim().toUpperCase();
    const value = Number(rate);
    if (!/^[A-Z]{3}$/.test(code)) {
      toast('Enter a three-letter currency code, such as USD.', 'err');
      return;
    }
    if (!(value > 0)) {
      toast('Enter a rate greater than zero.', 'err');
      return;
    }
    setSaving(true);
    try {
      await api.fx.upsert({ currency: code, rate: value, note: note.trim() });
      toast(`${code} rate saved.`, 'ok');
      setCurrency('');
      setRate('');
      setNote('');
      await load();
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setSaving(false);
    }
  }, [currency, load, note, rate, toast]);

  const remove = useCallback(
    async (code: string): Promise<void> => {
      // Removing a rate cannot restate an existing claim — every submitted row
      // carries the rate it was converted at — so this only stops new claims.
      if (!window.confirm(`Remove the ${code} rate? New ${code} receipts will be blocked until a rate is set again.`)) {
        return;
      }
      try {
        await api.fx.remove({ currency: code });
        toast(`${code} rate removed.`, 'ok');
        await load();
      } catch (err) {
        toast(errorMessage(err), 'err');
      }
    },
    [load, toast],
  );

  if (error) return <ErrorNote message={error} />;
  if (!data) return <LoadingBlock label="Loading exchange rates…" />;

  const columns: ReadonlyArray<Column<FxRate>> = [
    { key: 'currency', header: 'Currency', cell: (r) => <b>{r.currency}</b> },
    {
      key: 'rate',
      header: `${HOME_CURRENCY} per 1 unit`,
      align: 'right',
      cell: (r) => formatRate(r.rate),
    },
    { key: 'note', header: 'Note', cell: (r) => r.note || '—' },
    { key: 'updated', header: 'Last changed', cell: (r) => fmtDateTime(r.updatedAt) },
    { key: 'by', header: 'By', cell: (r) => r.updatedByName || '—' },
    ...(data.canEdit
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            cell: (r: FxRate) => (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setCurrency(r.currency);
                  setRate(String(r.rate));
                  setNote(r.note);
                }}
              >
                Edit
              </button>
            ),
          },
          {
            key: 'remove',
            header: '',
            align: 'right' as const,
            cell: (r: FxRate) => (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => void remove(r.currency)}
              >
                Remove
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <QueuePanel title="Exchange Rates" count={data.rates.length}>
        <p className="hint">
          Rates are stated as <b>{HOME_CURRENCY} per one unit</b> of the foreign currency. A receipt
          in a currency listed here is converted automatically when it is read, and the rate used is
          shown on the claim so approvers can see it. The rate is frozen onto a claim when it is
          submitted — changing a rate here never restates a claim that has already been approved.
        </p>
        <DataTable
          rows={data.rates}
          columns={columns}
          rowKey={(r) => r.currency}
          empty={
            <EmptyState
              icon="💱"
              title="No exchange rates have been set."
              description="Until a currency has a rate, receipts in it are flagged and cannot be submitted."
            />
          }
        />
      </QueuePanel>

      {data.canEdit ? (
        <div className="panel">
          <h3>Add or update a rate</h3>
          <div className="fx-form">
            <label>
              Currency
              <input
                value={currency}
                maxLength={3}
                placeholder="USD"
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              />
            </label>
            <label>
              {HOME_CURRENCY} per 1 unit
              <input
                type="number"
                step="0.000001"
                min="0"
                inputMode="decimal"
                value={rate}
                placeholder="58.50"
                onChange={(e) => setRate(e.target.value)}
              />
            </label>
            <label className="fx-note">
              Note (optional)
              <input
                value={note}
                maxLength={200}
                placeholder="Source and date of this rate"
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void save()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save rate'}
            </button>
          </div>
          <p className="hint">
            Every change is written to the audit trail with the old and new value.
          </p>
        </div>
      ) : null}
    </>
  );
}
